
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { MarginAnalysisState, MarginAnalysisActions } from './useMarginAnalysis';

interface ProjectionsSectionProps {
  state: MarginAnalysisState;
  actions: MarginAnalysisActions;
}

const ProjectionsSection: React.FC<ProjectionsSectionProps> = ({ state, actions }) => {
  const {
    pricePerUnit,
    discountRate,
    costPerUnit,
    pricePerUnitRange,
    costPerUnitRange,
    priceInputValue,
    discountInputValue,
    costInputValue
  } = state;

  const {
    setPricePerUnit,
    setDiscountRate,
    setCostPerUnit,
    handleReset,
    handlePriceInputChange,
    handleDiscountInputChange,
    handleCostInputChange
  } = actions;

  return (
    <Card className="bg-gradient-to-br from-[#1e293b] to-[#111827] border-primary/20 shadow-md hover:shadow-[#6366F1]/10 transition-all duration-300 rounded-md flex-1 py-0 h-[264px]">
      <CardContent className="p-4 flex flex-col h-full py-[4px]">
        <div className="flex justify-between items-center mb-2 py-0">
          <h3 className="text-[#D6BCFA] text-[15px] font-semibold my-0 py-0">Projections (use the slider bars)</h3>
          <Button onClick={handleReset} variant="outline" size="sm" className="transition-colors my-0 h-[20px] w-[55px] text-black font-normal text-[12px] bg-indigo-500 hover:bg-indigo-400 border-none">
            Reset
          </Button>
        </div>
        <div className="space-y-4 flex-1">
          <div>
            <Label htmlFor="price" className="text-[#6366F1] mb-1 flex justify-between">
              <span className="text-white text-[12px]">Price Per Unit</span>
              <h3 className="text-[#D6BCFA] text-[12px] text-white mb-2">
                ${Number(priceInputValue).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
              </h3>
            </Label>
            <div className="flex items-center gap-2">
              <input
                id="price"
                type="range"
                min={pricePerUnitRange.min}
                max={pricePerUnitRange.max}
                value={pricePerUnit}
                onChange={e => setPricePerUnit(Number(e.target.value))}
                className="flex-1 h-2 bg-[#111827] rounded-lg appearance-none cursor-pointer accent-[#6366F1] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#6366F1]"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="discount" className="text-[#EC4899] mb-1 flex justify-between">
              <span className="text-white text-[12px]">Discount Rate</span>
              <h3 className="text-[#D6BCFA] text-[12px] text-white mb-2">
                {Number(discountInputValue).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}%
              </h3>
            </Label>
            <div className="flex items-center gap-2">
              <input
                id="discount"
                type="range"
                min={0}
                max={90}
                value={discountRate}
                onChange={e => setDiscountRate(Number(e.target.value))}
                className="flex-1 h-2 bg-[#111827] rounded-lg appearance-none cursor-pointer accent-[#EC4899] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#EC4899]"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="cost" className="text-[#10B981] mb-1 flex justify-between">
              <span className="text-white text-[12px]">Cost Per Unit</span>
              <h3 className="text-[#D6BCFA] text-[12px] text-white mb-2">
                ${Number(costInputValue).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
              </h3>
            </Label>
            <div className="flex items-center gap-2">
              <input
                id="cost"
                type="range"
                min={costPerUnitRange.min}
                max={costPerUnitRange.max}
                value={costPerUnit}
                onChange={e => setCostPerUnit(Number(e.target.value))}
                className="flex-1 h-2 bg-[#111827] rounded-lg appearance-none cursor-pointer accent-[#10B981] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#10B981]"
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProjectionsSection;
